// All app stylesheets, in cascade order — see stylesheets/app.scss
import '@styles/app.scss';

import buttons from '@modules/buttons';


/**
 * Previously side-effect imports of `@commons/main`, whose only JS was two
 * conditional module loads now inlined below. b-breakout-copy, b-text-image-block
 * and b-video are omitted deliberately — already imported above.
 */

// Use double RAF to ensure styles are fully calculated before modules initialize
// This prevents forced reflows when modules read layout properties (clientHeight, etc.)
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    // Critical modules (keep static or ensure they load fast if needed globally)
    buttons();

    /**
     * The dropdown's keyboard behaviour, loaded on first contact with the nav
     * rather than on every page load.
     *
     * `focusin` is the important trigger, and it is bound to the whole header
     * rather than the nav deliberately. The logo sits outside `__nav`, and it is
     * the only focusable before the dropdown trigger — so listening on the nav
     * would fire at the exact moment focus lands on the trigger, giving the
     * import no time to resolve before a Space or Down. Listening on the header
     * buys that one Tab. (Binding on `keydown`, as the old interaction bundle
     * did, would swallow the keypress outright.)
     *
     * `pointerenter` covers the mouse case so `aria-expanded` is correct before
     * anyone hovers. Both are on the header, not the window, so a page nobody
     * touches never loads it.
     */
    const nav = document.querySelector('.b-site-header');
    if (nav) {
      const loadNavDropdown = () => {
        import('@modules/navDropdown').then(module => module.default());
        nav.removeEventListener('focusin', loadNavDropdown);
        nav.removeEventListener('pointerenter', loadNavDropdown);
      };
      nav.addEventListener('focusin', loadNavDropdown, { once: true });
      nav.addEventListener('pointerenter', loadNavDropdown, { once: true, passive: true });
    }

    /**
     * Defer stickyNav until the user shows intent to scroll.
     *
     * `scroll` alone loads the module *during* the first scroll, so the header
     * has no sticky behaviour until the chunk has been fetched and parsed — on a
     * slow connection that is the whole of the first gesture. `pointerdown` and
     * `touchstart` fire before any scrolling begins, which buys the fetch a head
     * start. `scroll` stays as the fallback for keyboard and programmatic
     * scrolling, which produce no pointer event at all.
     */
    const STICKY_NAV_TRIGGERS = ['pointerdown', 'touchstart', 'scroll'];
    const loadStickyNav = () => {
      import('@modules/stickyNav').then(module => module.default());
      STICKY_NAV_TRIGGERS.forEach(type => window.removeEventListener(type, loadStickyNav));
    };
    STICKY_NAV_TRIGGERS.forEach(type => {
      window.addEventListener(type, loadStickyNav, { passive: true, once: true });
    });

    // Dynamic imports based on DOM presence
    /**
     * Loads a module if anything on the page matches. Replaces the previous
     * loadModule/loadModules pair, which were logically identical —
     * `querySelector(s)` is truthy exactly when `querySelectorAll(s).length > 0`
     * — and were picked between inconsistently across nine call sites.
     */
    const loadModule = (selector, moduleName) => {
      if (document.querySelector(selector)) {
        import(`@modules/${moduleName}.js`).then(module => module.default());
      }
    };

    /**
     * `rootMargin` is a trade-off, not a constant: too tight and the chunk
     * arrives after the element is on screen, too loose and it is spent on
     * content the visitor never reaches. 500px is ~60% of a 823px viewport ahead,
     * which suits small chunks where guessing wrong costs little. Heavy chunks
     * get a tighter value at their call site — see `GLOBE_ROOT_MARGIN`.
     */
    const lazyLoad = (selector, callback, options = { rootMargin: '500px' }) => {
      const elements = document.querySelectorAll(selector);
      if (elements.length === 0) return;

      let fired = false;
      const observer = new IntersectionObserver((entries, obs) => {
        if (fired || !entries.some(entry => entry.isIntersecting)) return;
        fired = true;
        obs.disconnect();
        callback();
      }, options);
      elements.forEach(el => observer.observe(el));
    };

    const lazyLoadModule = (selector, moduleName, options) => {
      lazyLoad(selector, () => {
        import(`@modules/${moduleName}.js`).then(m => m.default());
      }, options);
    };

    loadModule('[data-customcookieyes]', 'consentChecker');
    loadModule('.js-modal-notification', 'modalNotification');
    loadModule('.b-toast', 'toast');
    loadModule('.js-team-grid', 'shuffleTeam');
    loadModule('.e-social-list__accept-cookies, .e-social-list__no-cookies', 'socialIcons');
    loadModule('.b-video__embed-wrapper--vimeo', 'vimeoDnt');
    loadModule('.js-video-control', 'videoControls');
    loadModule('.js-project-filter', 'projectFilter');
    loadModule('.js-project-hero-carousel__swiper', 'projectHeroCarousel');
    loadModule('.js-codepen-embed', 'codepenEmbed');
    /**
     * Not lazy-loaded on intersection: the listener has to already be attached
     * when a frame announces its height, and `loading="lazy"` starts the fetch
     * well before the embed is on screen. The Facebook embeds also depend on
     * this module for their src at all. Only loads on pages carrying one.
     */
    loadModule('.b-social-embed__iframe-wrapper iframe, iframe[data-facebook-post]', 'socialEmbed');
    loadModule('.b-code-snippet', 'prism');

    // Formie and related - Lazy loaded
    lazyLoad('.fui-form', () => {
      import('@modules/formieCached').then(m => m.default());
      import('@modules/formie').then(m => m.default());
      import('@modules/linkedInSubmission').then(m => m.default());
      import('@modules/gtmSubmission').then(m => m.default());
      import('@modules/campaignTracker').then(m => m.default());
    });
    lazyLoadModule('.js-chevron-carousel', 'chevronCarousel');
    lazyLoadModule('.js-grid-thumb', 'projectGrid');
    lazyLoadModule('.b-project-listing, .js-project-card', 'projectCard');
    lazyLoadModule('.js-text-image-block-swiper, .js-project-swiper, .js-contact-swiper, .js-testimonial-swiper, .js-full-width-image-carousel', 'swiperCarousel');
    /**
     * globe is by far the heaviest chunk on the site — ~1.1 MB raw, ~303 KB
     * gzipped, several times the entire rest of the page. At the shared 500px it
     * is fetched well before the map is on screen, and on any visit that stops
     * short of it those bytes are pure waste. 150px still starts the fetch before
     * the map scrolls in, without committing a third of a megabyte on spec.
     */
    const GLOBE_ROOT_MARGIN = { rootMargin: '150px' };
    lazyLoadModule('.c-globe__map', 'globe', GLOBE_ROOT_MARGIN);

    // GLightbox - Click loaded
    document.addEventListener('click', (e) => {
      const lightboxTrigger = e.target.closest('.js-lightbox');
      if (lightboxTrigger) {
        e.preventDefault();
        import('@modules/glightbox').then(m => m.default(lightboxTrigger));
        return;
      }

      const scrollToFormTrigger = e.target.closest('.b-landing-banner__content--button .e-button');
      if (scrollToFormTrigger) {
        // href first: the getElementById only runs once the cheap check passes.
        const href = scrollToFormTrigger.getAttribute('href');
        if (href && href.includes('/contact') && document.getElementById('form-contactForm')) {
          e.preventDefault();
          import('@modules/scrollToForm').then(m => m.default(scrollToFormTrigger, e));
        }
      }
    });
  });
});
